import {
  AssetInventorySection as AssetInventorySubTab,
  BankruptcyChecklistSection as BankruptcyChecklistSubTab,
} from "@shared";

import { ClientsFilterType } from "@/pages/clients/clients/Clients.types";
import { buildClientsFilterSearchParams } from "@/pages/clients/clients/Clients.utils";

import { AppRoutes } from "./AppRoutes";

export enum ClientRoutes {
  Index = "",
  ById = ":id",
  NoteById = ":noteId",
}

export enum ClientSearchParam {
  Search = "search",
  Salutation = "salutation",
  Title = "title",
  Name = "name",
  Birthday = "birthday",
  InsuranceNumber = "insuranceNumber",
  CaseNumber = "caseNumber",
  SrvFileNumber = "srvFileNumber",
  CourtId = "courtId",
  Street = "street",
  ZipCode = "zipCode",
  City = "city",
  Nationality = "nationality",
  MediaInquiry = "mediaInquiry",
  CaseNoteDateFrom = "caseNoteDateFrom",
  CaseNoteDateTo = "caseNoteDateTo",
  CaseNoteKeyword = "caseNoteKeyword",
  AssignedCounselorId = "assignedCounselorId",
  Status = "status",
}

export const clientPath = {
  root: (filter: ClientsFilterType | null): URLSearchParams | undefined => {
    const params = filter !== null ? buildClientsFilterSearchParams(filter) : undefined;
    return params;
  },

  my: (currentUserCounselorId: string | undefined): URLSearchParams => {
    const params = new URLSearchParams();
    if (currentUserCounselorId) {
      params.set(ClientSearchParam.AssignedCounselorId, currentUserCounselorId);
    }

    return params;
  },

  byId: (params: ClientByIdParams) => `${AppRoutes.Clients}/${params.id}`,

  byTab: (params: ClientTabParams) => `${AppRoutes.Clients}/${params.id}/${params.tab}`,

  caseNotePopout: (params: CaseNoteParams) =>
    `${AppRoutes.Clients}/${params.id}/${ClientTab.Notes}/${params.noteId}/${CASE_NOTE_POPOUT_SEGMENT}`,

  caseNote: (params: CaseNoteParams) =>
    `${AppRoutes.Clients}/${params.id}/${ClientTab.Notes}?${CASE_NOTE_ID_PARAM}=${params.noteId}`,

  srvProceeding: (params: SrvProceedingParams) =>
    `${AppRoutes.Clients}/${params.id}/${ClientTab.DebtSettlement}/${DebtSettlementSubTab.SrvProceeding}?${SRV_PROCEEDING_ID_PARAM}=${params.proceedingId}`,

  byMasterDataTab: (params: ClientByMasterDataTabParams) =>
    `${AppRoutes.Clients}/${params.id}/${ClientTab.MasterData}/${params.tab}`,

  byDebtSettlementTab: (params: ClientByDebtSettlementTabParams) =>
    `${AppRoutes.Clients}/${params.id}/${ClientTab.DebtSettlement}/${params.tab}`,

  byAssetInventorySubTab: (params: ClientByAssetInventorySubTabParams) =>
    `${AppRoutes.Clients}/${params.id}/${ClientTab.DebtSettlement}/${DebtSettlementSubTab.AssetInventory}/${params.subTab}`,

  byBankruptcyChecklistSubTab: (params: ClientByBankruptcyChecklistSubTabParams) =>
    `${AppRoutes.Clients}/${params.id}/${ClientTab.DebtSettlement}/${DebtSettlementSubTab.BankruptcyChecklist}/${params.subTab}`,

  search: (query: string): URLSearchParams => {
    const params = new URLSearchParams();
    params.set(ClientSearchParam.Search, query);

    return params;
  },
};

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type ClientByIdParams = {
  id: string;
};

export enum ClientTab {
  Overview = "overview",
  MasterData = "master-data",
  ClientNote = "client-note",
  Notes = "notes",
  DebtSettlement = "debt-settlement",
}

type ClientTabParams = ClientByIdParams & { tab: ClientTab };

export const CASE_NOTE_POPOUT_SEGMENT = "popout";

export const CASE_NOTE_ID_PARAM = "caseNoteId";

type CaseNoteParams = ClientByIdParams & { noteId: string };

export const SRV_PROCEEDING_ID_PARAM = "proceedingId";

/** Reserved SRV_PROCEEDING_ID_PARAM value while a new Verfahren is drafted; nothing is saved until "Speichern". */
export const NEW_SRV_PROCEEDING_ID = "new";

type SrvProceedingParams = ClientByIdParams & { proceedingId: string };

export enum ClientMasterDataTab {
  PersonalData = "personal-data",
  GoalsResults = "goals-results",
  MeasuresCauses = "measures-causes",
  StatusCounselor = "status-counselor",
  VolunteerAssignment = "volunteer-assignment",
  ClientAccess = "client-access",
}

type ClientByMasterDataTabParams = ClientByIdParams & {
  tab: ClientMasterDataTab;
};

export enum DebtSettlementSubTab {
  AssetInventory = "assets",
  Claims = "claims",
  BankruptcyChecklist = "bankruptcy-checklist",
  Compensation = "compensation",
  PersonalBankruptcyPetition = "personal-bankruptcy-petition",
  SrvProceeding = "srv-proceeding",
}

type ClientByDebtSettlementTabParams = ClientByIdParams & {
  tab: DebtSettlementSubTab;
};

// The section enums of the shared client-file areas moved to @shared (clientFileRoutes.ts) so the
// shared navigation components can build links in both apps; re-exported under their historical
// names so existing internal imports keep working.
export { AssetInventorySubTab, BankruptcyChecklistSubTab };

type ClientByAssetInventorySubTabParams = ClientByIdParams & {
  subTab: AssetInventorySubTab;
};

type ClientByBankruptcyChecklistSubTabParams = ClientByIdParams & {
  subTab: BankruptcyChecklistSubTab;
};
